/**
 * Màn **Cài đặt** — cấu hình đổi được lúc đang chạy.
 *
 * Hai luật hình dạng, cả hai đều từ `AP-44` (*"một dropdown không thể có hiệu lực là một lời nói
 * dối, không phải một mặc định"*):
 *
 * 1. **Ô chưa ai đặt phải nói là chưa ai đặt.** Hiện mặc định của máy như thể người dùng đã chọn
 *    là một lời nói dối nhỏ mà về sau không ai gỡ ra được — production của bản mẫu từng lưu
 *    `lessonDistiller=gpt-5` trong khi Claude làm việc, và chỉ một `log.warn` ghi lại.
 * 2. **Danh sách model đã THU HẸP theo tác vụ**, không phải toàn bộ danh mục. Tác vụ chỉ chạy
 *    được trên Anthropic thì dropdown chỉ hiện Anthropic.
 *
 * Và một thứ **cố ý không sửa được ở đây**: `PUBLISH_MODE`. Nó nằm ở `.env`, đổi phải khởi động
 * lại tiến trình. Đó là công tắc tổng cho 5 website khách hàng thật đang chạy production — một
 * nút trên trình duyệt bật được nó là quá dễ.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type MucCaiDat } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import {
  Bao,
  Chip,
  CongTac,
  DangTai,
  DauTrang,
  ViSao,
  HuyHieuLive,
  Khoi,
  Nut,
  The,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

const NHOM: { tieuDe: string; phu: string; loc: (k: string) => boolean }[] = [
  {
    tieuDe: "Công tắc lớn",
    phu: "Hai nút này quyết định hệ có tự chạy và có tiêu tiền hay không",
    loc: (k) =>
      k === "ai.dungModelGia" ||
      k === "job.chayTuDong" ||
      k === "job.chuKyGiay" ||
      k === "noiDung.khuMuiAi",
  },
  {
    tieuDe: "Vòng xoay model viết bài",
    phu: "Ba ô, luân phiên đều — bài này sang bài khác. Tắt một ô thì vòng bỏ qua, KHÔNG bỏ lượt",
    loc: (k) => k.startsWith("ai.xoayVong."),
  },
  {
    tieuDe: "Trần chi phí",
    phu: "Chạm trần thì dừng SINH bài mới, KHÔNG dừng đăng bài đã duyệt",
    loc: (k) => k.startsWith("chiPhi."),
  },
  {
    tieuDe: "Đăng thật — từng site một",
    phu: "Chỉ có tác dụng khi PUBLISH_MODE=live trong .env. Hai tầng, vì đây là website khách hàng thật",
    loc: (k) => k.startsWith("publish.") || k.startsWith("facebook."),
  },
  {
    tieuDe: "Model cho từng tác vụ",
    phu: "Bỏ trống = dùng mặc định của tác vụ. Công tắc model đóng thế ĐÈ LÊN tất cả",
    loc: (k) => k.startsWith("ai.model."),
  },
];

/**
 * Khoá **cố ý không hiện ở đây** vì đã có màn riêng, kèm chỗ nó thật sự nằm.
 *
 * Khai ra thay vì để chúng rơi lặng lẽ qua mọi bộ lọc: một khoá không khớp nhóm nào sẽ vô hình,
 * và "vô hình" trông hệt như "chưa được thêm vào". Danh sách này biến chuyện đó thành một câu
 * nói rõ, và cho phép cảnh báo bên dưới bắt được khoá thật sự bị bỏ quên.
 */
const AN_VI_CO_MAN_RIENG: { khop: (k: string) => boolean; o: string }[] = [
  { khop: (k) => k.startsWith("prompt."), o: "Cấu trúc prompt" },
];

/**
 * Gom model theo nhóm, **giữ nguyên thứ tự xuất hiện trong danh mục**.
 *
 * Không sắp lại theo bảng chữ cái: thứ tự trong `ai-catalog.ts` là thứ tự có chủ ý (model mới
 * trước, đời cũ sau), và sắp lại làm mất thông tin đó.
 */
function gomTheoNhom<T extends { nhom: string }>(ds: T[]): [string, T[]][] {
  const m = new Map<string, T[]>();
  for (const x of ds) {
    const co = m.get(x.nhom);
    if (co) co.push(x);
    else m.set(x.nhom, [x]);
  }
  return [...m.entries()];
}

export function CaiDat() {
  const qc = useQueryClient();
  const toast = useToast();
  const [nhap, datNhap] = useState<Record<string, string>>({});
  const [vuaLuu, datVuaLuu] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({ queryKey: khoa.caiDat, queryFn: api.caiDat });

  const luu = useMutation({
    mutationFn: (v: { khoa: string; giaTri: unknown }) => api.datCaiDat(v.khoa, v.giaTri),
    onSuccess: (kq) => {
      datVuaLuu(kq.khoa);
      datNhap((cu) => {
        const m = { ...cu };
        delete m[kq.khoa];
        return m;
      });
      void qc.invalidateQueries({ queryKey: khoa.caiDat });
      void qc.invalidateQueries({ queryKey: khoa.chiPhiTongQuan });
      void qc.invalidateQueries({ queryKey: khoa.health });

      // Lưu THÀNH CÔNG nhưng có điều cần biết — nhóm Telegram thường sắp đổi chat id, trần chi
      // phí cao hơn mọi lần nạp từ trước. Không phải lỗi (chip "đã lưu" vẫn đúng), nhưng nuốt nó
      // là mất luôn thông tin: mấy chuyện này chỉ lộ ra khi đã quá muộn.
      for (const c of kq.canhBao ?? []) toast.bao("thuong", kq.khoa, c);
    },
    // Chip "đã lưu" ngay tại hàng vẫn giữ — nó là phản hồi ĐÚNG CHỖ cho một lượt lưu thành công.
    // Lỗi thì khác: nó cần đọc, và chỗ đọc được là toast ở đáy màn.
    onError: (loi: Error, v) => toast.loi(`Không lưu được ${v.khoa}`, loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Cài đặt" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Cài đặt" />
        <DangTai />
      </div>
    );
  }

  const modelGiaBat = data.muc.find((m) => m.khoa === "ai.dungModelGia")?.giaTri === true;

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Cài đặt"
        trangThai={<HuyHieuLive live={data.publishMode === "live"} />}
        moTa="Nhịp đăng, trần chi phí và model đều là ô nhập ở đây, không phải hằng số trong code."
      />

      {/*
        Ba khối này là **trạng thái thường trực**, không phải kết quả một hành động — nên chúng ở
        lại trên trang chứ không đi bằng toast. Nhưng phần LÝ DO của mỗi khối đã chuyển vào
        `<ViSao>`: đợt 20, Lynust nêu *"phải đọc quá nhiều mới làm được"*, và ba đoạn văn đứng
        giữa anh và ô nhập đầu tiên chính là thứ anh phải đọc qua mỗi lần vào màn này.
      */}
      <Bao muc={data.publishMode === "live" ? "canh" : "thuong"} tieuDe={`PUBLISH_MODE = ${data.publishMode}`}>
        {data.publishMode === "live" ? (
          <>
            Công tắc tổng đang <strong>MỞ</strong> — bài lên website thật ở những site bật bên dưới.
            <ViSao nhan="site chưa bật thì sao">
              Site nào chưa bật thì vẫn <code>dry</code>: công tắc tổng chỉ mở cổng, từng site vẫn
              tự quyết.
            </ViSao>
          </>
        ) : (
          <>
            Công tắc tổng đang <strong>ĐÓNG</strong> — không có gì rời khỏi máy này.
            <ViSao nhan="kể cả khi site bên dưới đã bật">
              Đúng vậy, kể cả khi công tắc từng site bên dưới đang bật. Đổi phải sửa <code>.env</code>{" "}
              rồi khởi động lại tiến trình; cố ý không sửa được từ trình duyệt.
            </ViSao>
          </>
        )}
      </Bao>

      {modelGiaBat && (
        <Bao muc="on" tieuDe="Đang chạy bằng model đóng thế — $0">
          Mọi lựa chọn model bên dưới <strong>không có hiệu lực</strong> cho tới khi tắt công tắc này.
          <ViSao nhan="vì sao một công tắc đè lên tất cả">
            Mười một khoá rời là mười một chỗ quên tắt, và chỗ quên đầu tiên sẽ là chỗ tiêu tiền
            thật trong khi mọi màn hình vẫn báo $0.
          </ViSao>
        </Bao>
      )}

      {/*
        Lynust hỏi thẳng: "chỗ nào bắt buộc phải là các model của Claude thì phải cho tôi lời giải
        thích rõ ràng". Câu trả lời đứng NGAY TRÊN chỗ chọn model — không nằm trong một file tài
        liệu mà không ai mở lúc đang chọn.
      */}
      {data.rangBuocNhaCungCap.length > 0 && (
        <Bao
          muc="thuong"
          tieuDe={`${data.rangBuocNhaCungCap.length}/${data.soTacVu} tác vụ bắt buộc dùng model Claude`}
        >
          {data.rangBuocNhaCungCap.map((r) => (
            <div key={r.tacVu} className="mt-1 min-w-0">
              <code className="text-2xs font-medium text-muc-800">{r.tacVu}</code>
              <p className="mt-0.5 break-words text-2xs">{r.lyDo}</p>
            </div>
          ))}
          <p className="mt-2 text-2xs text-muc-500">
            <strong>{data.soTacVu - data.rangBuocNhaCungCap.length} tác vụ còn lại không có ràng
            buộc nào</strong> — chọn được bất kỳ model nào trong {data.danhMucModel.length} model
            của cả ba hãng.
          </p>
        </Bao>
      )}

      {/* Lỗi lưu đi bằng toast — xem `onError`. Màn này dài và ô nhập vừa sửa thường nằm cuối
          trang, nên một `<Bao>` ở đầu trang là thông điệp không ai nhìn thấy. */}

      <NhipDang />

      {(() => {
        // Khoá nào không thuộc nhóm nào và cũng không được khai là "có màn riêng" thì nó đang vô
        // hình. Một ô cấu hình vô hình vẫn được dây chuyền đọc — nói ra còn hơn để nó im lặng.
        const moCoi = data.muc.filter(
          (m) => !NHOM.some((n) => n.loc(m.khoa)) && !AN_VI_CO_MAN_RIENG.some((a) => a.khop(m.khoa)),
        );
        return moCoi.length === 0 ? null : (
          <Bao muc="canh" tieuDe={`${moCoi.length} khoá chưa thuộc nhóm nào`}>
            {moCoi.map((m) => m.khoa).join(" · ")} — chúng vẫn được đọc lúc chạy nhưng không hiện ở
            màn nào cả. Thêm chúng vào một nhóm, hoặc khai vào danh sách &ldquo;có màn riêng&rdquo;.
          </Bao>
        );
      })()}

      {NHOM.map((nhom, i) => {
        const muc = data.muc.filter((m) => nhom.loc(m.khoa));
        if (muc.length === 0) return null;
        return (
          // Ba nhóm đầu mở sẵn; nhóm model có 11 tác vụ nên gập lại — bắt cuộn qua 11 dropdown
          // mỗi lần vào Cài đặt là thuế thu hằng ngày.
          <Khoi
            key={nhom.tieuDe}
            nhan={nhom.tieuDe}
            id={`cai-dat-${i}`}
            moSan={i < 3}
            tomTat={nhom.phu}
          >
            <The>
              <div className="space-y-4">
                {muc.map((m) => (
                  <OCaiDat
                    key={m.khoa}
                    m={m}
                    banNhap={nhap[m.khoa]}
                    vuaLuu={vuaLuu === m.khoa}
                    // Chỉ hàng vừa bấm mới báo đang chạy. `isPending` một mình làm **mọi** ô trên
                    // màn Cài đặt cùng khoá và cùng quay — trông y như cả trang bị treo.
                    dangLuu={luu.isPending && luu.variables?.khoa === m.khoa}
                    moNhat={m.kieu === "model" && modelGiaBat}
                    datBanNhap={(v) => datNhap((cu) => ({ ...cu, [m.khoa]: v }))}
                    luu={(giaTri) => luu.mutate({ khoa: m.khoa, giaTri })}
                  />
                ))}
              </div>
            </The>
          </Khoi>
        );
      })}
    </div>
  );
}

function OCaiDat({
  m,
  banNhap,
  vuaLuu,
  dangLuu,
  moNhat,
  datBanNhap,
  luu,
}: {
  m: MucCaiDat;
  banNhap: string | undefined;
  vuaLuu: boolean;
  dangLuu: boolean;
  moNhat: boolean;
  datBanNhap: (v: string) => void;
  luu: (giaTri: unknown) => void;
}) {
  const ten = m.khoa.split(".").slice(-1)[0];

  /**
   * Có gì để lưu chưa — quyết định nút Lưu là **cam hay viền**.
   *
   * So với giá trị đang lưu, không chỉ so với rỗng: gõ lại đúng con số cũ thì không có gì để lưu,
   * và một nút cam ở đó là cam nói dối. Cam trong app này mang đúng một nghĩa — *có việc cần
   * Lynust*.
   */
  const coThayDoi =
    banNhap !== undefined &&
    banNhap.trim() !== "" &&
    banNhap.trim() !== String(m.giaTri ?? "").trim();

  return (
    <div className={moNhat ? "min-w-0 opacity-50" : "min-w-0"}>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <code className="text-2xs text-muc-500">{m.khoa}</code>
        {!m.daDat &&
          (m.phaiChon ? (
            // Cam mang đúng một nghĩa: có việc cần Lynust. Ô này Lynust đã chốt là phải tự chọn,
            // nên trống ở đây là một việc chưa làm, không phải một mặc định đang yên ổn.
            <Chip muc="loi" title="Lynust chốt: ô này phải tự chọn, không chạy bằng mặc định">
              CHƯA CHỈ ĐỊNH — đang chạy {m.modelMacDinh ?? "mặc định"}
            </Chip>
          ) : (
            <Chip muc="canh" title="Máy đang dùng mặc định — chưa ai chọn giá trị này">
              chưa ai đặt
            </Chip>
          ))}
        {m.daDat && m.nguoiSua && <Chip muc="thuong">{m.nguoiSua} sửa</Chip>}
        {vuaLuu && <Chip muc="on">đã lưu</Chip>}
      </div>

      <p className="mt-0.5 min-w-0 break-words text-2xs text-muc-500">{m.moTa}</p>

      <div className="mt-2 min-w-0">
        {/*
          MỘT công tắc, không phải hai nút "Bật"/"Tắt".
          Hai nút bắt người dùng đọc xem nút nào đang được tô để biết trạng thái hiện tại — tức là
          suy ngược trạng thái từ hình dạng của lời kêu gọi hành động. Công tắc nói thẳng.
        */}
        {m.kieu === "bool" && (
          <CongTac
            bat={m.giaTri === true}
            doi={(ke) => luu(ke)}
            khoa={dangLuu}
            dangChay={dangLuu}
            nhanDocMan={m.khoa}
          />
        )}

        {m.kieu === "so" && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              step="0.01"
              min={m.toiThieu ?? undefined}
              max={m.toiDa ?? undefined}
              value={banNhap ?? String(m.giaTri ?? "")}
              onChange={(e) => datBanNhap(e.target.value)}
              className={`${lopO} max-w-36`}
              aria-label={ten}
            />
            {/* Cam chỉ khi có gì để lưu — xem ghi chú cùng luật ở màn Cấu trúc prompt. */}
            <Nut
              kieu={coThayDoi ? "chinh" : "vien"}
              dangChay={dangLuu}
              disabled={!coThayDoi}
              onClick={() => luu(Number(banNhap))}
            >
              Lưu
            </Nut>
            {(m.toiThieu !== null || m.toiDa !== null) && (
              <span className="text-2xs text-muc-400">
                {m.toiThieu ?? "?"} – {m.toiDa ?? "?"}
              </span>
            )}
          </div>
        )}

        {/*
          Ô chữ: gốc URL từng site, username bot. Trước 09/09/2026 chúng chỉ đặt được bằng lệnh
          `dat-cai-dat` vì màn này và CLI giữ hai danh sách khoá khác nhau — nay chung một danh
          mục, nên thiếu nhánh này là màn hình BỎ QUA 6 khoá trong im lặng.
        */}
        {m.kieu === "chuoi" && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              spellCheck={false}
              value={banNhap ?? String(m.giaTri ?? "")}
              onChange={(e) => datBanNhap(e.target.value)}
              className={`${lopO} w-full max-w-96`}
              aria-label={ten}
            />
            <Nut
              kieu={coThayDoi ? "chinh" : "vien"}
              dangChay={dangLuu}
              disabled={!coThayDoi}
              onClick={() => luu((banNhap ?? "").trim())}
            >
              Lưu
            </Nut>
          </div>
        )}

        {m.kieu === "model" && (
          <div className="flex flex-wrap items-center gap-2">
            {/*
              Gom theo NHÓM (Claude Opus · GPT-5 · Gemini Flash…). 32 model trong một danh sách
              phẳng là một danh sách không ai đọc hết — và Lynust chốt "show toàn bộ các AI theo
              từng phiên bản", nên không được cắt bớt, chỉ được sắp cho đọc được.
            */}
            <select
              value={banNhap ?? String(m.giaTri ?? "")}
              onChange={(e) => {
                datBanNhap(e.target.value);
                luu(e.target.value);
              }}
              className={`${lopO} max-w-md`}
              aria-label={ten}
            >
              <option value="">(mặc định: {m.modelMacDinh ?? "?"})</option>
              {gomTheoNhom(m.modelChoPhep ?? []).map(([nhom, ds]) => (
                <optgroup key={nhom} label={nhom}>
                  {ds.map((mm) => (
                    <option key={mm.catalogKey} value={mm.catalogKey}>
                      {mm.catalogKey}
                      {mm.cu ? " (đời cũ)" : ""} — ${mm.giaVaoUsdMoiTrieu}/${mm.giaRaUsdMoiTrieu}
                      {" mỗi 1M"}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {m.modelChoPhep !== null && m.modelChoPhep.length < (m.tongSoModel ?? 99) && (
              <span className="text-2xs text-canh-600">
                danh sách đã thu hẹp — xem lý do ở đầu trang
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Nhịp đăng mỗi site
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Số bài mỗi kỳ và **đơn vị kỳ** — ngày hay tuần.
 *
 * ⚠️ **Nguồn sự thật là `SiteProfile`, không phải collection `CaiDat`.** `lich-dang.service.ts`
 * đọc hạn ngạch từ đó, nên chép con số sang `CaiDat` để "cho gọn màn hình" là tạo ra hai nguồn
 * sự thật cho cùng một con số — và chúng sẽ lệch nhau vào đúng ngày không ai để ý. Màn này chỉ
 * **hiển thị và ghi** vào nguồn duy nhất đó.
 *
 * Ô chọn ngày/tuần trước 09/09/2026 **không tồn tại trên giao diện** dù backend đã nhận
 * `donViKy` đủ cả — và nhãn thì ghi cứng "Bài mỗi ngày", nên một site đặt đơn vị `tuần` hiện ra
 * một câu sai mà không lỗi nào nổ.
 */
function NhipDang() {
  const qc = useQueryClient();
  const [sua, datSua] = useState<Record<string, { so: string; donVi: string }>>({});
  const [vuaLuu, datVuaLuu] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({ queryKey: khoa.hoSoSite, queryFn: api.hoSoSite });

  const luu = useMutation({
    mutationFn: (v: { site: string; moTaChuDe: string; baiMoiKy: number; donViKy: string }) =>
      api.suaHoSoSite(v.site, {
        moTaChuDe: v.moTaChuDe,
        baiMoiKy: v.baiMoiKy,
        donViKy: v.donViKy,
      }),
    onSuccess: (kq) => {
      datVuaLuu(kq.siteSlug);
      datSua((cu) => {
        const m = { ...cu };
        delete m[kq.siteSlug];
        return m;
      });
      void qc.invalidateQueries({ queryKey: khoa.hoSoSite });
    },
  });

  if (isPending) {
    return (
      <Khoi nhan="Nhịp đăng mỗi site" id="cai-dat-nhip" moSan tomTat="…">
        <DangTai />
      </Khoi>
    );
  }
  if (error) {
    return (
      <Khoi nhan="Nhịp đăng mỗi site" id="cai-dat-nhip" moSan>
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </Khoi>
    );
  }

  const moiNgay = data.reduce(
    (t, h) => t + (h.donViKy === "tuan" ? h.baiMoiKy / 7 : h.baiMoiKy),
    0,
  );

  return (
    <Khoi
      nhan="Nhịp đăng mỗi site"
      id="cai-dat-nhip"
      moSan
      tomTat={`${Math.round(moiNgay * 10) / 10} bài/ngày trên cả 5 site`}
    >
      <p className="text-xs text-muc-600">
        Hạn ngạch đọc từ hồ sơ site, <strong>không hardcode</strong>. Đặt 0 là site đó ngừng sinh
        bài mới — bài đã duyệt vẫn đăng theo lịch.
      </p>

      <The>
        <div className="space-y-3">
          {data.map((h) => {
            const ban = sua[h.siteSlug];
            const so = ban?.so ?? String(h.baiMoiKy);
            const donVi = ban?.donVi ?? h.donViKy;
            const daDoi = ban !== undefined;
            const soN = Number(so);
            const hopLe = so.trim() !== "" && Number.isInteger(soN) && soN >= 0 && soN <= 50;

            return (
              <div
                key={h.siteSlug}
                className="flex min-w-0 flex-wrap items-end justify-between gap-3 border-b border-muc-100 pb-3 last:border-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-muc-900">
                    {h.siteSlug}
                    {vuaLuu === h.siteSlug && !daDoi && (
                      <Chip muc="on">
                        <span className="ml-1.5">đã lưu</span>
                      </Chip>
                    )}
                  </p>
                  <p className="text-xs text-muc-500">
                    {h.moTaChuDe.trim() === "" ? (
                      <span className="text-canh-600">
                        chưa có mô tả — site này sẽ ném lỗi khi tới lượt
                      </span>
                    ) : (
                      `${h.soBaiDaViet} bài đã viết`
                    )}
                  </p>
                  {!hopLe && (
                    <p className="text-2xs text-loi-600">Phải là số nguyên từ 0 đến 50.</p>
                  )}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={50}
                    value={so}
                    disabled={luu.isPending}
                    onChange={(e) =>
                      datSua((cu) => ({ ...cu, [h.siteSlug]: { so: e.target.value, donVi } }))
                    }
                    className={`${lopO} w-20 text-right so-thang-cot`}
                  />
                  <span className="text-xs text-muc-500">bài mỗi</span>
                  <select
                    value={donVi}
                    disabled={luu.isPending}
                    onChange={(e) =>
                      datSua((cu) => ({ ...cu, [h.siteSlug]: { so, donVi: e.target.value } }))
                    }
                    className={`${lopO} w-28`}
                  >
                    <option value="ngay">ngày</option>
                    <option value="tuan">tuần</option>
                  </select>
                  <Nut
                    kieu={daDoi ? "chinh" : "vien"}
                    dangChay={luu.isPending && luu.variables?.site === h.siteSlug}
                    disabled={!daDoi || !hopLe}
                    onClick={() =>
                      luu.mutate({
                        site: h.siteSlug,
                        // Mô tả gửi lại NGUYÊN VẸN. Trường này bắt buộc ở DTO, nên gửi chuỗi rỗng
                        // để "chỉ đổi hạn ngạch" sẽ xoá trắng câu mô tả của site — và không lỗi
                        // nào nổ, chỉ có bài sau viết sai giọng.
                        moTaChuDe: h.moTaChuDe,
                        baiMoiKy: soN,
                        donViKy: donVi,
                      })
                    }
                  >
                    Lưu
                  </Nut>
                </div>
              </div>
            );
          })}
        </div>

        {luu.error && (
          <Bao muc="loi" tieuDe="Không lưu được">
            {luu.error.message}
          </Bao>
        )}
      </The>

      <p className="mt-2 text-2xs text-muc-500">
        Câu <strong>&ldquo;site này viết về cái gì&rdquo;</strong> ở màn Chân dung khách — đó là
        câu đi vào prompt, không phải cấu hình vận hành.
      </p>
    </Khoi>
  );
}
